import {Injectable} from "@angular/core";
import {Camera, PerspectiveCamera, Vector3} from "three";
import {LamellaAreaObject} from "../objects-3D/lamellaAreaObject";

export type CameraType = 'perspective'; // | 'orthographic' | 'cube';
export type ViewPortCamera<T> = T extends Camera ? T : never;

@Injectable({providedIn: 'root'})
export class CameraService {

    //strategy pattern map
    private readonly strategies = {
        "perspective": this.newPerspectiveCamera
    }

    //provide new camera based on the strategy pattern
    public new(type: CameraType, element?: HTMLElement) { return this.strategies[type](element); }

    private newPerspectiveCamera(element?: HTMLElement): PerspectiveCamera {
        const camera = new PerspectiveCamera();
        camera.aspect = element ? element?.clientWidth / element?.clientHeight : 1;
        camera.fov = 60;
        camera.near = 0.01;
        camera.far = 2000;
        camera.filmGauge = 135;
        camera.updateProjectionMatrix();
        return camera;
    }

    /**
     *
     * @param camera PerspectiveCamera
     * @param object lamellaAreaObject
     * @param factor viewportWidth / canvasWidth; default 1.0
     */
    public returnToAnchor(camera: PerspectiveCamera, object: LamellaAreaObject, factor: number = 1.0): Vector3 {

        const angle = 53; //[degrees]

        const viewAngle = Math.sin((angle * Math.PI) / 180); //[radians]
        const lamellaSize = object.size;
        const lamellaWidth = lamellaSize.x;

        //get distance to fit the object in the camera fov and scale it to 60% (closer). Then apply factor to scale it to the viewport
        const distance = this.getCameraDistanceToFitWidth(lamellaWidth, camera) * 0.6 / factor;

        //vectorize length to components
        const lengthX = distance * viewAngle;
        const lengthZ = distance * (1-viewAngle);

        //Since lamellaAreaObject children are not centered around lamellaAreaObject position
        const realCenterPosition = object.worldPosition.clone().add(object.center);

        camera.zoom = 1;

        //height set at 75% of the height of the object
        camera.position.set(realCenterPosition.x+lengthX, realCenterPosition.y * 1.25, lengthZ);

        camera.lookAt(realCenterPosition);

        return realCenterPosition;
    }

    /**
     * calculates to distance to fit the object in the camera view
     * @param objectSpan
     * @param camera
     */
    public getCameraDistanceToFitWidth(objectSpan: number, camera: PerspectiveCamera): number {
        const aspect = camera.aspect;
        const vFOV = (camera.fov * Math.PI) / 180;                 // [rad] convert vertical fov to radians
        const hFOV = 2 * Math.atan(Math.tan(vFOV / 2) * aspect);   // [rad] Convert vertical FOV to horizontal FOV
        return (  objectSpan / 2 ) / Math.tan( hFOV / 2 );
    }


    public updateAspect(camera: PerspectiveCamera, element: HTMLElement): void {
        camera.aspect = element.clientWidth / element.clientHeight;
        camera.updateProjectionMatrix();
    }

    private cachedCanvasRect: DOMRect | null = null;

    public updateViewportCamera(camera: ViewPortCamera<any>, viewport: HTMLElement, canvas: HTMLElement): void {
        if(!(camera instanceof PerspectiveCamera)) {
            console.warn("couldn't update viewport camera");
            return;
        }

        if(!this.cachedCanvasRect){
            this.cachedCanvasRect = canvas.getBoundingClientRect();
        }

        // Get bounding rectangles of canvas and viewport
        const canvasRect =  this.cachedCanvasRect;
        const viewportRect = viewport.getBoundingClientRect();

        // Calculate the offset needed to correctly center the viewport within the canvas
        const offsetX = (canvasRect.width/2) + canvasRect.left - (viewportRect.width/2);
        const offsetY = (canvasRect.height/2) + canvasRect.top - (viewportRect.height/2);

        // Calculate the new values for the viewport
        const fullWidth = canvasRect.width;
        const fullHeight = canvasRect.height;
        const width = viewportRect.width;
        const height = viewportRect.height;

        // Set the view offset on the camera with aspect ratio
        camera.aspect = height / width;
        camera.setViewOffset(fullWidth, fullHeight, offsetX, offsetY, width, height);
        camera.updateProjectionMatrix();
    }


}